import { GEN_SITE } from "@api/constants";
import activateLicense2 from "@api/operations/activate-license-2";
import { CFC } from "@api/types/cypress";
import Router from "next/router";
import React, { useState } from "react";

import Button from "@components/Button";
import SelectInput from "@components/SelectInput";

const LicenseActivationForm: CFC<{ token: string; sku: string }> = ({ token, sku, cy }) => {
  const [licenseCode, setLicenseCode] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showCourseSelect, setShowCourseSelect] = useState(false);
  const [courseSelectOptions, setCourseSelectOptions] = useState([]);

  // Genie has multiple sites.
  // Need site attribute to know what site the enrollment is going to be created for.
  const defaultAttributes = () => {
    const attrs = [
      { code: "site", value: GEN_SITE },
      { code: "allowReenrollment", value: "true" },
    ];

    if (sku.length > 0) {
      attrs.push({ code: "selectedCourseSku", value: sku });
    }

    return attrs;
  };
  const [attributes, setAttributes] = useState(defaultAttributes());

  const activate = async () => {
    const payload = await activateLicense2(token, licenseCode, attributes);
    if (!payload) {
      setError(true);
      setErrorMessage("Failed to activate license code.");
      return;
    }

    const { licensable, statusCode, reason } = payload;
    const { key, value } = reason;

    // activated successfully
    if (statusCode === 200 && licensable && licensable.customerId) {
      await Router.push("/courses/purchased");
    }
    // eligible to re-enroll
    else if (statusCode === 200 && key === "REENROLLMENT_ELIGIBLE" && licensable) {
      setError(true);
      setErrorMessage(value);

      // Push re-enrollment request
      const copyAttr = attributes.filter((a) => a.code !== "allowReenrollment");
      copyAttr.push({ code: "reenrollmentRequest", value: "true" });
      setAttributes(copyAttr);

      console.log(statusCode, key, reason);
    }
    // selectable course license
    else if (statusCode === 400 && key === "NO_COURSE_SELECTED" && licensable) {
      // clear error message
      setError(false);
      setErrorMessage("");

      const { courses } = licensable;
      const options = [
        { label: "Select Course", value: "" },
        ...courses.map((c) => ({
          label: `${c.courseSku} - ${c.courseName.length > 0 ? c.courseName : c.courseSku}`,
          value: c.courseSku,
        })),
      ];

      setCourseSelectOptions(options);
      setShowCourseSelect(true);
    }
    // failed
    else {
      setError(true);
      setErrorMessage(value.length > 0 ? value : "Failed to activate license code.");
    }
  };

  return (
    <>
      <p className="license-format">License format example: XXXX-XXXX-XXXX-XXXX or XXXXXXXXXXXX</p>
      <div className="activation-form" data-cy={cy}>
        <input
          type="text"
          value={licenseCode}
          onChange={(e) => {
            setLicenseCode(e.target.value);
          }}
        />
        {showCourseSelect && (
          <div className="course-select">
            <p>Please select a course and click activate again:</p>
            <SelectInput
              label=""
              name="course"
              onChange={(value) => {
                let newAttributes = [...attributes];

                if (value && value.length > 0) {
                  // remove all selectedCourseSku attributes
                  newAttributes = newAttributes.filter((a) => a.code !== "selectedCourseSku");

                  newAttributes.push({ code: "selectedCourseSku", value });
                }

                setAttributes(newAttributes);
              }}
              options={courseSelectOptions}
              cy="course"
            />
          </div>
        )}
        {error && <div className="activation-error">{errorMessage}</div>}
        <div className="activation-button">
          <Button onClick={() => activate()}>Activate</Button>
        </div>
        <div className="cancel-button">
          <Button theme="secondary" onClick={() => window.history.back()}>
            Cancel
          </Button>
        </div>
      </div>
      <style jsx>{`
        .container {
          margin-left: 0;
          margin-right: 0;
          padding: 0;
          display: inline-block;
          position: relative;
          width: 100%;
          box-sizing: border-box;
          background-color: rgb(255, 255, 255);
        }

        .activation-container {
          margin-top: 0;
          margin-left: 0;
          padding: 20px 30px;
          display: inline-block;
          position: relative;
          box-sizing: border-box;
          height: auto;
          vertical-align: top;
          width: 100%;
        }

        .activation-header {
          display: block;
          padding-bottom: 24px;
          border-bottom: 1px solid rgb(194, 194, 194);
        }

        .activation-header h2 {
          cursor: default;
          font-size: 24px;
          margin: 0;
          text-align: center;
        }

        .license-format {
          text-align: center;
          font-style: italic;
        }

        .activation-form {
          margin: 0 auto;
        }

        .activation-error {
          color: red;
          text-align: center;
          padding-top: 1em;
        }

        .activation-button {
          padding: 1em 0 0;
          margin: 0 auto;
          width: 50%;
        }

        .cancel-button {
          padding: 1em 0 0;
          margin: 0 auto;
          width: 50%;
        }

        input {
          width: 100%;
          padding-left: 20px;
          height: 49px;
          margin-bottom: 20px;
        }

        :global(.course-select select) {
          height: 49px;
        }

        :global(.course-select .Select) {
          border: 1px solid rgb(59, 59, 59) !important;
          border-radius: 2px;
        }

        @media screen and (min-width: 768px) {
          .activation-form {
            width: 500px;
          }

          input {
            margin-bottom: 0;
          }
        }
      `}</style>
    </>
  );
};

export default LicenseActivationForm;
